'use client';

import type {PropsWithChildren} from 'react';
import Script from 'next/script';
import {SessionProvider} from 'next-auth/react';
import {ThemeProvider} from 'next-themes';
import {SidebarProvider} from '@manaboo/ui/sidebar';
import {TooltipProvider} from '@manaboo/ui/tooltip';
import {Toaster} from '@manaboo/ui/toaster';
import {AuthDialogProvider} from '@/hooks/use-dialog-auth';
import {CookieBanner} from '@/components/cookie-banner/cookie-banner';
import {
	CookieConsentProvider,
	useCookieConsent,
} from '@/components/cookie-banner/cookie-context';

const GA_ID = 'G-7V28EVXH9Y';

export function Providers({children}: PropsWithChildren) {
	return (
		<CookieConsentProvider>
			<SessionProvider>
				<ThemeProvider
					attribute="class"
					defaultTheme="system"
					enableSystem
					disableTransitionOnChange
				>
					<SidebarProvider defaultOpen={false}>
						<TooltipProvider>
							<AuthDialogProvider>{children}</AuthDialogProvider>
							<Toaster />
						</TooltipProvider>
					</SidebarProvider>
				</ThemeProvider>
			</SessionProvider>

			<CookieBanner />

			<AnalyticsScripts />
		</CookieConsentProvider>
	);
}

function AnalyticsScripts() {
	const {consent} = useCookieConsent();

	if (!consent?.analytics) return null;

	return (
		<>
			{/* Google Analytics */}
			<Script
				strategy="afterInteractive"
				src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
			/>

			<Script
				id="google-analytics"
				strategy="afterInteractive"
				dangerouslySetInnerHTML={{
					__html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', '${GA_ID}');
                    `,
				}}
			/>

			{/* Hotjar */}
			<Script
				id="hotjar"
				strategy="afterInteractive"
				dangerouslySetInnerHTML={{
					__html: `
                        (function(h,o,t,j,a,r){
                            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                            h._hjSettings={hjid:5299369,hjsv:6};
                            a=o.getElementsByTagName('head')[0];
                            r=o.createElement('script');r.async=1;
                            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                            a.appendChild(r);
                        })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
                    `,
				}}
			/>

			{/* Chatbase */}
			{/* 1. Inline initializer */}
			<Script
				id="chatbase-init"
				strategy="afterInteractive"
			>
				{`
				  (function(){
					if (!window.chatbase || window.chatbase("getState") !== "initialized") {
					  window.chatbase = (...args) => {
						if (!window.chatbase.q) window.chatbase.q = [];
						window.chatbase.q.push(args);
					  };
					  window.chatbase = new Proxy(window.chatbase, {
						get(target, prop) {
						  if (prop === "q") return target.q;
						  return (...args) => target(prop, ...args);
						}
					  });
					}
					const onLoad = () => {
					  const script = document.createElement("script");
					  script.src = "https://www.chatbase.co/embed.min.js";
					  script.id = "n2dA5pPAMBOlD_urqvphi";
					  script.domain = "www.chatbase.co";
					  document.body.appendChild(script);
					};
					if (document.readyState === "complete") {
					  onLoad();
					} else {
					  window.addEventListener("load", onLoad);
					}
				  })();
				`}
			</Script>

			<Script
				id="chatbase-embed"
				src="https://www.chatbase.co/embed.min.js"
				strategy="lazyOnload"
				data-domain="www.chatbase.co"
			/>
		</>
	);
}
